import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';
import { GameModeService } from '../../_services/game-mode.service';
import { MiniGameService } from '../../_services/mini-game.service';
import { GamePhase } from '../../interfaces/game-state.interface';
import { MiniGameType } from '../../interfaces/mini-game.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { TranslateService } from '@ngx-translate/core';
import { GameAudioService } from '../../_services/game-audio.service';

@Component({
  selector: 'app-game-overlay',
  templateUrl: './game-overlay.component.html',
  styleUrls: ['./game-overlay.component.scss'],
  animations: [
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('0.4s cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('0.3s ease-in', style({ transform: 'translateY(100%)' }))
      ])
    ])
  ]
})
export class GameOverlayComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  phase: GamePhase = GamePhase.IDLE;
  GamePhase = GamePhase;
  MiniGameType = MiniGameType;
  currentGameType: MiniGameType | null = null;
  currLang: any = 'en';

  isLeaderboardOpen = true;
  showPreGameCountdown = false;
  preGameCountdownValue = 3;
  countdownAnimating = true;
  private countdownAudio: HTMLAudioElement | null = null;
  private countdownInterval: any = null;

  // Countdown visual effects
  burstLines: number[] = Array.from({ length: 24 }, (_, i) => i * 15);
  particles: { x: string; y: string; delay: number; size: number; color: string }[] = [];

  private generateParticles(): void {
    const colors = ['#FFD700', '#FF6B35', '#00E5FF', '#FF4081', '#76FF03', '#FFAB00'];
    this.particles = Array.from({ length: 18 }, () => ({
      x: (Math.random() * 200 - 100) + 'px',
      y: (Math.random() * 200 - 100) + 'px',
      delay: Math.random() * 400,
      size: 3 + Math.random() * 5,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
  }

  constructor(
    public gameModeService: GameModeService,
    public miniGameService: MiniGameService,
    private globalService: GlobalService,
    private translate: TranslateService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    document.body.classList.add('game-overlay-active');

    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currLang = lang;
        this.translate.use(lang as any);
      });

    this.gameModeService.gamePhase$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(phase => {
        const wasNotActive = this.phase !== GamePhase.MINI_GAME_ACTIVE;
        this.phase = phase;
        if (phase === GamePhase.MINI_GAME_ACTIVE && wasNotActive) {
          this.startPreGameCountdown();
        }
      });

    this.miniGameService.currentGameType$
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.currentGameType = type;
      });

    this.miniGameService.leaderboardOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(open => this.isLeaderboardOpen = open);
  }

  ngOnDestroy(): void {
    document.body.classList.remove('game-overlay-active');
    this.clearCountdown();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startPreGameCountdown(): void {
    this.showPreGameCountdown = true;
    this.preGameCountdownValue = 3;
    this.countdownAnimating = true;
    this.generateParticles();
    this.gameAudioService.stop(this.countdownAudio);
    this.countdownAudio = this.gameAudioService.play('assets/game-mode/sound-effects/countDownTimer.mp3');
    this.countdownInterval = setInterval(() => {
      // Toggle off to destroy DOM, then back on in next frame to recreate with fresh animations
      this.countdownAnimating = false;
      this.preGameCountdownValue--;
      if (this.preGameCountdownValue <= 0) {
        this.clearCountdown();
        this.showPreGameCountdown = false;
        return;
      }
      requestAnimationFrame(() => {
        this.generateParticles();
        this.countdownAnimating = true;
      });
    }, 800);
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.gameAudioService.stop(this.countdownAudio);
    this.countdownAudio = null;
  }

  get showGameHeader(): boolean {
    return (this.phase === GamePhase.MINI_GAME_ACTIVE && !this.showPreGameCountdown)
      || this.phase === GamePhase.MINI_GAME_SUMMARY
      || this.phase === GamePhase.TEAM_SETUP
      || this.phase === GamePhase.QUIZ
      || this.phase === GamePhase.QUIZ_RESULT
      || this.phase === GamePhase.QUIZ_SUMMARY;
  }

  get showBackButton(): boolean {
    return !this.showPreGameCountdown && (
      (this.phase === GamePhase.MINI_GAME_ACTIVE)
      || this.phase === GamePhase.MINI_GAME_SUMMARY
      || this.phase === GamePhase.TEAM_SETUP
      || this.phase === GamePhase.QUIZ
      || this.phase === GamePhase.QUIZ_RESULT
      || this.phase === GamePhase.QUIZ_SUMMARY
    );
  }

  onSelectGame(): void {
    this.miniGameService.disableAutoCycle();
    this.gameModeService.autoStartOnEntry = false;
    this.miniGameService.stopTimer();

    if (this.phase === GamePhase.QUIZ || this.phase === GamePhase.QUIZ_RESULT || this.phase === GamePhase.QUIZ_SUMMARY) {
      this.gameModeService.stopQuizTimer();
      this.gameModeService.finishQuiz();
    } else {
      // finishMiniGame will go to SUBJECT_PICK since auto-cycle is now disabled
      this.miniGameService.finishMiniGame();
    }
  }

  closeGame(): void {
    this.miniGameService.disableAutoCycle();
    this.miniGameService.stopTimer();
    this.gameModeService.stopQuizTimer();
    this.miniGameService.finishMiniGame();
    this.gameModeService.closeGame();
  }
}
